import type { DateValue } from '@internationalized/date';
import * as changeCase from 'change-case';

import type {
  CustomerSegment,
  DunningState,
  InvoiceStatus,
  Partner,
  User,
} from '@stuut/api/models';
import { formatCurrency } from '@stuut/common/src/utils';

import { ValueSelectorType } from './filterGroup';
import type { Field, ValueSelector } from './filterGroup';

export const assigneeValueSelector: ValueSelector = {
  formatValue: (value: User | User[]) => {
    if (!Array.isArray(value)) {
      return value?.name ?? null;
    }

    if (value.length > 2) {
      return `${value.length} assignees`;
    }
    return value.map((user) => user.name).join(', ');
  },

  toJsonQueryValue: (value: User | User[]) => {
    return Array.isArray(value) ? value.map((user) => user.id) : value?.id;
  },

  type: ValueSelectorType.Assignee,
};

export const countryValueSelector: ValueSelector = {
  formatValue: (value: string[]) => {
    if (value.length > 3) {
      return `${value.length} countries`;
    }
    return value.map((countryCode) => countryCode).join(', ');
  },

  toJsonQueryValue: (value: string[]) => {
    return value;
  },

  type: ValueSelectorType.Country,
};

export const monetaryValueSelector: ValueSelector = {
  formatValue: (value: number) => {
    return `${formatCurrency({ value })}`;
  },

  toJsonQueryValue: (value: number) => {
    return value;
  },

  type: ValueSelectorType.Monetary,
};

export const numericValueSelector: ValueSelector = {
  formatValue: (value: number) => {
    if (value !== null && value !== undefined) {
      const formatter = new Intl.NumberFormat('en-US', {
        notation: 'standard',
      });

      return formatter.format(value);
    }

    return '';
  },

  toJsonQueryValue: (value: number) => {
    return value;
  },

  type: ValueSelectorType.Numeric,
};

export const partnerInformationValueSelector: ValueSelector = {
  formatValue: (value: { label: string; value: string }[]) => {
    if (value.length > 3) {
      return `${value.length} information types`;
    }
    return value.map((option) => option.label).join(', ');
  },

  toJsonQueryValue: (value: { label: string; value: string }[]) => {
    return value ? value.map((option) => option.value) : [];
  },
  type: ValueSelectorType.PartnerInformation,
};

export const customerValueSelector: ValueSelector = {
  formatValue: (value: Partner | Partner[]) => {
    if (!Array.isArray(value)) {
      return value?.computedName;
    }

    if (value.length > 3) {
      return `${value.length} customers`;
    }
    return value.map((partner) => partner.computedName).join(', ');
  },

  toJsonQueryValue: (value: Partner | Partner[]) => {
    return Array.isArray(value) ? value.map((partner) => partner.id) : value?.id;
  },

  type: ValueSelectorType.Customer,
};

export function enumValueSelector<T extends { [name: string]: any }>(enumType: T): ValueSelector {
  return {
    formatValue: (value: InvoiceStatus[]) => {
      if (value.length > 3) {
        return `${value.length} selected`;
      }
      return value.map((status) => changeCase.capitalCase(status)).join(', ');
    },

    props: {
      enum: enumType,
    },

    toJsonQueryValue: (value: InvoiceStatus[]) => {
      return value;
    },

    type: ValueSelectorType.Enum,
  };
}

export const invoiceStatusValueSelector: ValueSelector = {
  formatValue: (value: InvoiceStatus[]) => {
    if (value.length > 3) {
      return `${value.length} selected`;
    }
    return value.map((status) => changeCase.capitalCase(status)).join(', ');
  },

  toJsonQueryValue: (value: InvoiceStatus[]) => {
    return value;
  },

  type: ValueSelectorType.InvoiceStatus,
};

export const dateValueSelector: ValueSelector = {
  formatValue: (value?: DateValue) => {
    return value ? `${value.toString()}` : '';
  },

  toJsonQueryValue: (value?: DateValue) => {
    return value?.toString();
  },

  type: ValueSelectorType.Date,
};

export const booleanValueSelector: ValueSelector = {
  formatValue: (value, field: Field) => {
    return field.label.toLowerCase();
  },

  toJsonQueryValue: (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? '1' : '0';
    }

    return value;
  },

  type: ValueSelectorType.Boolean,
};

export const literalBooleanValueSelector: ValueSelector = {
  formatValue: (value, field: Field) => {
    return field.label.toLowerCase();
  },

  toJsonQueryValue: (value: boolean) => {
    return value;
  },

  type: ValueSelectorType.Boolean,
};

export const textValueSelector: ValueSelector = {
  formatValue: (value: string) => {
    return value ?? '';
  },

  toJsonQueryValue: (value: string) => {
    return value;
  },

  type: ValueSelectorType.Text,
};

export const segmentValueSelector: ValueSelector = {
  formatValue: (value: CustomerSegment) => {
    return value?.name ?? '';
  },

  toJsonQueryValue: (value: CustomerSegment) => {
    return value?.id;
  },

  type: ValueSelectorType.Segment,
};

export const businessUnitValueSelector: ValueSelector = {
  formatValue: (value: { id: string; name: string }[]) => {
    if (value.length > 2) {
      return `${value.length} business units`;
    }
    return value.map((bu) => bu.name).join(', ');
  },

  toJsonQueryValue: (value: { id: string; name: string }[]) => {
    return value.map((bu) => bu.id);
  },

  type: ValueSelectorType.BusinessUnit,
};

export const dunningStateValueSelector: ValueSelector = {
  formatValue: (value: DunningState[]) => {
    if (value.length > 2) {
      return `${value.length} dunning states`;
    }
    return value.map((state) => state.name).join(', ');
  },

  toJsonQueryValue: (value: DunningState[]) => {
    const ids = value.map((state) => state.id);
    // Partners with no dunning state are implicitly in "Dunning" state,
    // so include empty id to match them when "Dunning" is selected
    if (value.some((state) => state.name === 'Dunning')) {
      ids.push('');
    }
    return ids;
  },

  type: ValueSelectorType.DunningState,
};

export const currencyValueSelector: ValueSelector = {
  formatValue: (value: string[]) => {
    if (value.length > 3) {
      return `${value.length} currencies`;
    }
    return value.map((currency) => currency.toUpperCase()).join(', ');
  },

  toJsonQueryValue: (value: string[]) => {
    return value;
  },

  type: ValueSelectorType.Currency,
};
